import type { StoredConversation, MessageEntry, ConversationStore } from './types';

const MAX_MESSAGES = 100;
const TTL_DAYS = 30;

function storageKey(siteId: string): string {
  return `ch_conv_${siteId}`;
}

export function leadStorageKey(siteId: string): string {
  return `ch_lead_${siteId}`;
}

/**
 * Short-lived cache key for the `enabled: false` state.
 * If the config endpoint returns disabled, we cache that for ~60s to avoid
 * hammering the API on every page load.
 */
export function disabledCacheKey(siteId: string): string {
  return `ch_disabled_${siteId}`;
}

const DISABLED_TTL_MS = 60_000;

export function cacheDisabled(siteId: string): void {
  try {
    localStorage.setItem(disabledCacheKey(siteId), String(Date.now() + DISABLED_TTL_MS));
  } catch {
    // localStorage unavailable — skip cache
  }
}

export function isDisabledCached(siteId: string): boolean {
  try {
    const raw = localStorage.getItem(disabledCacheKey(siteId));
    if (!raw) return false;
    const expiresAt = parseInt(raw, 10);
    if (isNaN(expiresAt) || Date.now() >= expiresAt) {
      localStorage.removeItem(disabledCacheKey(siteId));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function loadConversation(siteId: string): StoredConversation | null {
  try {
    const raw = localStorage.getItem(storageKey(siteId));
    if (!raw) return null;
    const data = JSON.parse(raw) as { id: string; expires_at: string; messages: unknown[] };
    if (new Date(data.expires_at) < new Date()) {
      localStorage.removeItem(storageKey(siteId));
      return null;
    }
    return {
      id: data.id,
      expires_at: data.expires_at,
      messages: (data.messages ?? []) as MessageEntry[],
    };
  } catch {
    return null;
  }
}

export function saveConversation(siteId: string, convId: string, messages: MessageEntry[]): void {
  const expires = new Date();
  expires.setDate(expires.getDate() + TTL_DAYS);
  const data: StoredConversation = {
    id: convId,
    expires_at: expires.toISOString(),
    messages: messages.slice(-MAX_MESSAGES),
  };
  try {
    localStorage.setItem(storageKey(siteId), JSON.stringify(data));
  } catch {
    // Storage full — silently fail
  }
}

export function clearConversation(siteId: string): void {
  localStorage.removeItem(storageKey(siteId));
}

export function createConversationStore(): ConversationStore {
  return {
    load: loadConversation,
    save: saveConversation,
    clear: clearConversation,
  };
}
